/**
 * Me → «Мои карты» → one chart: the wheel, Big-3, «Calestea чат» and the ⋯ menu (edit / delete).
 * The tables sit under a collapsed toggle, so the page fits one screen.
 *
 * The chat button opens the latest conversation about this chart, or a new one when there is none
 * (the list of conversations itself lives in the chat screen).
 *
 * Self chart: title «Я», edit goes to Settings → Account, cannot be deleted, chat is the
 * natal one (no chartId). Other people: chat carries `chartId` + `surface=chart`.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MeSubpageShell } from '../../../components/chrome/MeSubpageShell';
import { Big3Strip } from '../../../components/me/Big3Strip';
import { ChartView } from '../../../components/me/ChartView';
import { toChartViewData } from '../../../components/me/chartViewAdapter';
import { ConfirmModal } from '../../../components/settings/ConfirmModal';
import { useChartDetail, useDeleteChart, useUpdateChart, type ChartDetail } from '../../../hooks/useCharts';
import { useChatThreads } from '../../../hooks/useChatThreads';
import { formatBirthDate } from '../../../lib/format-date';
import { newChatHref, threadHref } from '../../../lib/chat-links';
import { PRECISION_TO_TIME_OF_DAY } from '../../../lib/birth-time';
import { relationDisplayKey } from '../../../lib/relation';
import { ChartMenu } from './ChartMenu';
import styles from './ChartPage.module.css';

/** The server names the own chart «Я» when the profile has no first name. */
const NO_NAME = 'Я';

function BirthLine({ chart }: { chart: ChartDetail }): JSX.Element {
  const { t, i18n } = useTranslation(['me', 'flows']);
  let time: string | null = null;
  if (chart.timePrecision === 'EXACT') {
    time = chart.timeExact;
  } else {
    const part = PRECISION_TO_TIME_OF_DAY[chart.timePrecision];
    time = part ? t(`flows:onboarding.birthData.timeOfDay.${part}`) : null;
  }
  const parts = [formatBirthDate(chart.date, i18n.language), time, chart.placeName].filter(Boolean);
  return (
    <p className={styles.birth} data-testid="chart-birth">
      {parts.join(' · ')}
    </p>
  );
}

/**
 * `children` (the ask button) go between the Big-3 and the tables; `footer` (the natal-chart link) follows
 * the toggle. While the wheel is not shown both follow the status line.
 */
function ChartBody({ chart, stalled, restartPolling, children, footer }: {
  chart: ChartDetail;
  stalled: boolean;
  restartPolling: () => void;
  children?: ReactNode;
  footer?: ReactNode;
}): JSX.Element {
  const { t } = useTranslation('me');
  const update = useUpdateChart(chart.id);
  const [busy, setBusy] = useState(false);
  const [retryFailed, setRetryFailed] = useState(false);

  const failed = chart.calcStatus === 'FAILED' || (chart.calcStatus === 'PENDING' && stalled);
  const pending = chart.calcStatus === 'PENDING' && !stalled;
  const ready = chart.calcStatus === 'READY';

  // Once the retry button is gone (the chart went back to PENDING) the next appearance starts enabled.
  useEffect(() => {
    if (!failed) setBusy(false);
  }, [failed]);

  const retry = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setRetryFailed(false);
    try {
      // Touching a birth field makes the server recompute the chart.
      const saved = await update.mutateAsync({ date: chart.date });
      // Stay disabled until the new status arrives. The server may answer FAILED right away
      // (queueing failed) — the status never changes then, so re-enable the button.
      if (saved.calcStatus === 'FAILED') setBusy(false);
      restartPolling();
    } catch {
      setRetryFailed(true);
      setBusy(false);
    }
  };

  const big3 = chart.big3 ? (
    <Big3Strip sun={chart.big3.sun} moon={chart.big3.moon} ascendant={chart.big3.asc} />
  ) : null;
  const viewData = ready ? toChartViewData(chart.positions) : null;

  return (
    <>
      {ready && viewData && (
        <ChartView data={viewData} header={big3} details={{ label: t('charts.page.detailsToggle'), after: footer }}>
          <div className={styles.below}>{children}</div>
        </ChartView>
      )}
      {ready && !viewData && big3}
      {pending && <p className={styles.note}>{t('charts.page.calculating')}</p>}
      {failed && (
        <div className={styles.statusRow}>
          <span className={styles.failed}>{t('charts.page.failed')}</span>
          <button
            type="button"
            className={styles.retry}
            onClick={() => void retry()}
            disabled={busy}
            data-testid="chart-retry"
          >
            {t('charts.retry')}
          </button>
        </div>
      )}
      {retryFailed && (
        <p role="alert" className={styles.error}>
          {t('charts.retryError')}
        </p>
      )}
      {!(ready && viewData) && (
        <>
          {children}
          {footer}
        </>
      )}
    </>
  );
}

export function ChartPage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['me', 'common']);
  const { chart, isLoading, notFound, stalled, refetch, restartPolling } = useChartDetail(id);
  const { threads, isLoading: threadsLoading } = useChatThreads(
    chart ? (chart.isSelf ? '' : chart.id) : undefined,
  );
  const del = useDeleteChart();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteFailed, setDeleteFailed] = useState(false);

  let title = t('charts.title');
  if (chart) {
    const relationLabel = t(relationDisplayKey(chart.relation, chart.isSelf, chart.sex));
    const hasName = chart.name.trim() !== '' && (!chart.isSelf || chart.name.trim() !== NO_NAME);
    title = chart.isSelf && !hasName ? relationLabel : `${chart.name} · ${relationLabel}`;
  }

  const backToList = (): void => void navigate('/me/charts');
  const goEdit = (): void => {
    if (!chart) return;
    void navigate(chart.isSelf ? '/settings/account' : `/me/charts/${chart.id}/edit`);
  };

  const confirmDelete = async (): Promise<void> => {
    if (deleting) return;
    setDeleting(true);
    setDeleteFailed(false);
    try {
      await del.mutateAsync(id);
      // replace: Back must not return to the page of the deleted chart.
      void navigate('/me/charts', { replace: true });
    } catch {
      setDeleteFailed(true);
      setDeleting(false);
    }
  };

  let content: JSX.Element;
  if (isLoading) {
    content = (
      <p className={styles.note} data-testid="chart-loading">
        {t('common:states.loading')}
      </p>
    );
  } else if (notFound) {
    content = (
      <div className={styles.stack}>
        <p role="alert" data-testid="chart-not-found">
          {t('charts.page.notFound')}
        </p>
        <button type="button" className={`${styles.retry} ${styles.back}`} onClick={backToList}>
          {t('charts.page.toList')}
        </button>
      </div>
    );
  } else if (!chart) {
    // A failed background poll keeps a loaded chart; the error shows only when there is nothing to show.
    content = (
      <div className={styles.stack}>
        <p role="alert" data-testid="chart-error">
          {t('charts.form.loadError')}
        </p>
        <button type="button" className={`${styles.retry} ${styles.back}`} onClick={refetch} data-testid="chart-error-retry">
          {t('charts.retry')}
        </button>
      </div>
    );
  } else {
    const ready = chart.calcStatus === 'READY';
    const askReason = ready
      ? null
      : chart.calcStatus === 'FAILED' || stalled
        ? t('charts.page.askFailed')
        : t('charts.page.askNeedsCalc');
    // The newest conversation first (the server sorts so); none yet → a new one.
    const latest = threads[0];
    const askHref = latest ? threadHref(latest.id) : newChatHref(chart);
    const count = chart.conversationsCount;
    const deleteBody =
      count === 0
        ? t('charts.delete.bodyNoConversations', { name: chart.name })
        : t('charts.delete.body', { count, name: chart.name });

    content = (
      <div className={styles.stack}>
        <BirthLine chart={chart} />
        <ChartBody
          chart={chart}
          stalled={stalled}
          restartPolling={restartPolling}
          footer={
            chart.isSelf ? (
              <button type="button" className={styles.link} onClick={() => void navigate('/me/natal-chart')}>
                {t('charts.page.natalLink')}
              </button>
            ) : undefined
          }
        >
          <div className={styles.askBlock}>
            <button
              type="button"
              className={styles.ask}
              disabled={!ready || threadsLoading}
              aria-describedby={askReason ? 'chart-ask-reason' : undefined}
              onClick={() => void navigate(askHref)}
              data-testid="chart-ask"
            >
              {t('charts.page.ask')}
            </button>
            {askReason && (
              <p id="chart-ask-reason" className={styles.askReason}>
                {askReason}
              </p>
            )}
          </div>
        </ChartBody>

        {confirmOpen && !chart.isSelf && (
          <ConfirmModal
            variant="danger"
            title={t('charts.delete.title')}
            body={deleteBody}
            confirmLabel={t('charts.delete.confirm')}
            cancelLabel={t('common:actions.cancel')}
            busy={deleting}
            error={deleteFailed ? t('charts.delete.error') : undefined}
            onConfirm={() => void confirmDelete()}
            onCancel={() => setConfirmOpen(false)}
          />
        )}
      </div>
    );
  }

  const menu = chart ? (
    <ChartMenu
      onEdit={goEdit}
      onDelete={
        chart.isSelf
          ? undefined
          : () => {
              setDeleteFailed(false);
              setConfirmOpen(true);
            }
      }
    />
  ) : undefined;

  return (
    <MeSubpageShell title={title} right={menu} singleLineTitle data-testid="chart-page">
      {content}
    </MeSubpageShell>
  );
}
